import React, { useState, useEffect } from 'react';
import {
  PageShell,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Button,
  Input,
} from '@resource-kit/ui/react';
import PasswordChangeForm from '@components/ui/PasswordChangeForm';
import { useAuth } from '@hooks/useAuth';
import { authClient } from '@lib/betterAuth';
import { formatDateTimeCompact } from '@lib/utils';
import { User, Mail, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Profile: React.FC = () => {
  const { user, refreshSession } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [formData, setFormData] = useState({ name: '', email: '' });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
      });
    }
  }, [user]);

  const handleInputChange = (field: 'name', value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await authClient.updateUser({ name: formData.name });
      await refreshSession();

      setSuccess(t('profile.updateSuccess'));
    } catch (err: unknown) {
      setError((err instanceof Error ? err.message : undefined) || t('profile.updateFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({ name: user.name || '', email: user.email || '' });
    }
    setError(null);
    setSuccess(null);
  };

  const handlePasswordChange = async (currentPassword: string, newPassword: string) => {
    try {
      setPasswordLoading(true);
      setPasswordError(null);
      setPasswordSuccess(null);

      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: true,
      });

      if (error) {
        setPasswordError(error.message || t('profile.passwordChangeFailed'));
        return { success: false, error };
      }

      setPasswordSuccess(t('profile.passwordChanged'));

      setTimeout(() => {
        refreshSession();
      }, 1000);

      return { success: true };
    } catch (err: unknown) {
      const errorMessage =
        (err instanceof Error ? err.message : undefined) || t('profile.passwordChangeFailed');
      setPasswordError(errorMessage);
      return { success: false, error: err };
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">{t('profile.loadingProfile')}</p>
      </div>
    );
  }

  return (
    // No page-private width cap and no page-private masthead: this was the
    // app's one bespoke opening (a 96px avatar disc beside a `text-3xl` name,
    // no page title at all, and `max-w-4xl mx-auto` stacked on the frame's own
    // padding, so its content edge lined up with nothing else). The name and
    // the address were the only information that opening carried, and both are
    // account fields — so they moved into the account card and the page kept an
    // ordinary title, like every other page. Owner decision, 2026-08-08.
    <PageShell title={t('profile.pageTitle')} subtitle={t('profile.subtitle')}>
      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              {t('profile.accountDetails')}
            </CardTitle>
            <CardDescription>{t('profile.accountDetailsDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="grid gap-6 md:grid-cols-2">
              <div className="space-y-1">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('profile.displayName')}
                </dt>
                <dd className="text-sm font-medium">{user.name || t('ui.none')}</dd>
              </div>
              <div className="space-y-1">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('profile.emailLabel')}
                </dt>
                <dd className="text-sm flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  {user.email}
                </dd>
              </div>
              <div className="space-y-1">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('profile.accountId')}
                </dt>
                <dd className="text-sm font-mono bg-muted p-1 rounded w-fit">{user.id}</dd>
              </div>
              <div className="space-y-1">
                <dt className="text-sm font-medium text-muted-foreground">{t('profile.role')}</dt>
                <dd className="text-sm font-medium capitalize">{user.role}</dd>
              </div>
              <div className="space-y-1">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('profile.emailVerified')}
                </dt>
                <dd className="text-sm flex items-center gap-2">
                  {user.emailVerified ? (
                    <span className="flex items-center text-success">
                      <CheckCircle className="w-4 h-4 mr-1" /> {t('profile.verified')}
                    </span>
                  ) : (
                    <span className="flex items-center text-warning">
                      <AlertCircle className="w-4 h-4 mr-1" /> {t('profile.unverified')}
                    </span>
                  )}
                </dd>
              </div>
              <div className="space-y-1">
                <dt className="text-sm font-medium text-muted-foreground">
                  {t('profile.lastUpdated')}
                </dt>
                <dd className="text-sm">{formatDateTimeCompact(new Date())}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('profile.editProfile')}</CardTitle>
            <CardDescription>{t('profile.editProfileDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('profile.displayName')}
                </label>
                <Input
                  value={formData.name}
                  onChange={(event) => handleInputChange('name', event.target.value)}
                  placeholder={t('profile.displayNamePlaceholder')}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t('profile.emailLabel')}
                </label>
                <Input value={formData.email} disabled className="bg-muted" />
                <p className="text-[10px] text-muted-foreground">{t('profile.emailManaged')}</p>
              </div>
            </div>

            {success && (
              <div className="mt-4 rounded-md bg-success/20 px-4 py-3 text-sm text-success flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {success}
              </div>
            )}
            {error && (
              <div className="mt-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div className="mt-6 flex space-x-4">
              <Button onClick={handleSave} disabled={loading}>
                {loading ? t('profile.saving') : t('profile.saveChanges')}
              </Button>
              <Button variant="outline" onClick={handleCancel}>
                {t('ui.cancel')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {t('profile.security')}
            </CardTitle>
            <CardDescription>{t('profile.securityDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <PasswordChangeForm onPasswordChange={handlePasswordChange} loading={passwordLoading} />

            {passwordSuccess && (
              <div className="mt-4 rounded-md bg-success/20 px-4 py-3 text-sm text-success flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {passwordSuccess}
              </div>
            )}
            {passwordError && (
              <div className="mt-4 rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {passwordError}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
};

export default Profile;
